"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> & {
  /**
   * Set false to render the content in place instead of portalling it to
   * `document.body`. Defaults to true (portalling is the right default — it
   * escapes ancestor `overflow`/stacking contexts).
   *
   * Opt out when the popover lives inside a MODAL Radix `Dialog` AND its
   * content scrolls. A modal Dialog wraps itself in `react-remove-scroll`,
   * which `preventDefault()`s any wheel/touch event whose target sits outside
   * the locked subtree (`shouldStop = !noIsolation` in its SideEffect). A
   * portalled popover is a sibling of the dialog on `document.body`, so it is
   * outside that subtree and its internal scroll container silently refuses
   * to scroll — no error, the wheel just does nothing. Radix's Dialog exposes
   * no `shards` escape hatch, so keeping the content inside the dialog's own
   * DOM is the fix. Safe here because `DialogContent` sets no `overflow`, and
   * Floating UI resolves the dialog's `translate` transform correctly when it
   * measures the offset parent.
   */
  portal?: boolean
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = "center", sideOffset = 4, portal = true, ...props }, ref) => {
  const content = (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]",
        className
      )}
      {...props}
    />
  )

  return portal ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent }
